import type { DurableJobExpandOpts, IDurableJob, IFetchDurableJobsParams, IUserConfirmation } from '@condo/domain';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { omit } from 'lodash';
import { getClient, type IApiClients } from '@/shared/api/client';

/** Basecamp can be slow to accept `importEstarcoreUnits` (and similar) on preview / under load; default 30s axios timeout is too tight. */
export const SCHEDULE_IMPORT_ESTARCORE_UNITS_TIMEOUT_MS = 120_000;

export const getDurableJobApi = async (jobId: string): Promise<IDurableJob> => {
    const { data } = await getClient('basecamp').get<{ durableJob: IDurableJob }>(`/durable-jobs/${jobId}`);
    return data.durableJob;
};

export const fetchDurableJobs = async (params?: IFetchDurableJobsParams): Promise<{ durableJobs: IDurableJob[]; total: number }> => {
    const { data } = await getClient('basecamp').get<{ durableJobs: IDurableJob[]; total: number }>('/durable-jobs', {
        params,
    });
    return data;
};

export const fetchDurableJobsList = async (): Promise<
    { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null; source: string }[]
> => {
    const { data } = await getClient('basecamp').get<{
        durableJobs: { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null }[];
    }>('/durable-jobs/list');
    return (data.durableJobs ?? []).map(job => ({ ...job, source: 'basecamp' }));
};

export const cancelDurableJob = async (jobUuid: string): Promise<void> => {
    await (getClient('basecamp') as AxiosInstance).delete(`/durable-jobs/${jobUuid}`);
};

export const rerunDurableJob = async (jobUuid: string): Promise<void> => {
    await (getClient('basecamp') as AxiosInstance).post(`/durable-jobs/${jobUuid}/rerun`);
};

export const scheduleDurableJob = async (
    params: {
        name: string;
        params?: unknown;
        meta?: unknown;
        opts?: { runAfter?: number; priority?: number };
    },
    client: keyof IApiClients,
    requestConfig?: AxiosRequestConfig,
): Promise<IDurableJob> => (getClient(client) as AxiosInstance).post('/durable-jobs', params, requestConfig).then(response => response.data.durableJob);

export const fetchUnseenDurableJobs = async (expand?: DurableJobExpandOpts): Promise<{ durableJobs: IDurableJob[]; total: number }> => {
    const { data } = await getClient('basecamp').get<{ durableJobs: IDurableJob[]; total: number }>('/durable-jobs/unseen', {
        params: { expand },
    });
    return data;
};

export const markDurableJobsSeen = async (ids: string[]): Promise<IDurableJob[]> => {
    const { data } = await getClient('basecamp').put<{ durableJobs: IDurableJob[] }>('/durable-jobs/seen', { ids });
    return data.durableJobs;
};

export const fetchUserConfirmations = async (): Promise<IUserConfirmation[]> => {
    const { data } = await getClient('basecamp').get<{ userConfirmations: IUserConfirmation[] }>('/user-confirmations');
    return data.userConfirmations;
};

export const settleUserConfirmation = async (query: Partial<IUserConfirmation>): Promise<void> => {
    if (!query.origin) {
        throw new Error('Need to have an origin for settling a user-confirmation');
    }
    if (query.origin.includes('basecamp')) {
        await getClient('basecamp').post(`/user-confirmations/${query.userConfirmationId}`, omit(query, 'origin'));
        return;
    }
    throw new Error(`Unsupported confirmation-origin: ${query.origin}`);
};
